import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { map, skipWhile, distinctUntilChanged, tap } from 'rxjs/operators';
import * as semver from 'semver';
import { ConfigHttpService } from '@app/core/async-services/http/static/config.http';
import { VersionConfig, Route, PageLimits } from './version-config.interface';
import { version, VersionInterface } from '@env/version';
import { Store } from '@ngrx/store';
import { State } from '@app/store';
import * as InboxActions from '@app/store/inbox/inbox.actions';
import * as InboxSelectors from '@app/store/inbox/inbox.selectors';

@Injectable({
  providedIn: 'root',
})
export class VersionConfigService {
  private _currentAppVersion: string;
  private _behaviorSubject = new BehaviorSubject<VersionConfig | null>(null);
  private _subject = new BehaviorSubject<boolean | null>(null);
  private _pageLimits$ = new BehaviorSubject<PageLimits | null>(null);
  private _isInMaintenance$ = new BehaviorSubject<boolean>(false);

  constructor(
    private _configHttpService: ConfigHttpService,
    private _store: Store<State>,
  ) {
    this._currentAppVersion = version.version!;
  }

  fetchVersionConfig() {
    return this._configHttpService
      .getApiVersionConfigurations(this._currentAppVersion)
      .pipe(
        map((response) => response.payload),
        tap((versionConfig) => {
          this.setVersionConfig(versionConfig);
          this.setAppVersionUpdateRequirementStatus(versionConfig, version);
          this._setPageLimits(versionConfig.app.pageLimits);
          this._setIsInMaintenance(versionConfig);
        }),
      );
  }

  getVersionConfig() {
    return this._behaviorSubject;
  }

  get inboxPageLimit(): number {
    return this._pageLimits$.value ? this._pageLimits$.value.inbox : 0;
  }

  get newsFeedPageLimit(): number {
    return this._pageLimits$.value ? this._pageLimits$.value.newsfeed : 0;
  }

  get messagesPageLimit(): number {
    return this._pageLimits$.value ? this._pageLimits$.value.messages : 0;
  }

  get isInMaintenance(): boolean {
    return this._isInMaintenance$.value;
  }

  get isInMaintenance$(): Observable<boolean> {
    return this._isInMaintenance$.asObservable().pipe(distinctUntilChanged());
  }

  getVersion(api: Route) {
    return this.getVersionConfig().pipe(
      skipWhile((config) => !config || !config.api?.active),
      map((config) => config?.api?.prefix[api]),
      distinctUntilChanged(),
    );
  }

  getAppVersionUpdateRequirementStatus() {
    return this._subject;
  }

  setAppVersionUpdateRequirementStatus(
    versionConfig: VersionConfig,
    versionObj: VersionInterface,
  ) {
    // IF: No suitable API
    if (!versionConfig.api?.active) {
      this._subject.next(true);
      return false;
    }
    // IF: Versions are out of sync, or there is an update available
    if (
      (versionConfig.app.version.priority === 'HIGH' &&
        semver.cmp(
          versionObj.version!,
          '>=',
          versionConfig.app.version.latest,
        ) === false) ||
      (!this._compareSemanticAppVersions(versionObj.version!, versionConfig) &&
        versionConfig.app.version.priority !== 'HIGH')
    ) {
      this._subject.next(true);
    } else {
      this._subject.next(false);
    }
  }

  setVersionConfig(versionConfig: VersionConfig): void {
    this._behaviorSubject.next(versionConfig);
  }

  clear(): void {
    this._behaviorSubject.next(null);
  }

  private _compareSemanticAppVersions(
    currentAppVersion: string,
    apiServerVersionConfig: VersionConfig,
  ) {
    const v = apiServerVersionConfig.api?.requirements?.version;

    if (v?.max != null) {
      // Current App Version is API Compatible
      return semver.satisfies(currentAppVersion, `${v?.min} - ${v?.max}`);
    }

    return true;
  }

  private async _setPageLimits(limits: PageLimits): Promise<void> {
    const channelsPerPage = await firstValueFrom(
      this._store.select(InboxSelectors.selectChannelsPerPage),
    );

    const didPageLimitsChange =
      channelsPerPage !== limits.inbox ||
      limits.inbox !== this.inboxPageLimit ||
      limits.messages !== this.messagesPageLimit ||
      limits.newsfeed !== this.newsFeedPageLimit;

    if (didPageLimitsChange) {
      this._pageLimits$.next(limits);
      this._store.dispatch(
        InboxActions.setPageLimit({ channelsPerPage: limits.inbox }),
      );
    }
  }

  private _setIsInMaintenance(config: VersionConfig): void {
    const isInMaintenance = config.app.isInMaintenance;
    this._isInMaintenance$.next(isInMaintenance);
  }
}
